import { createPortal } from "react-dom";
import React, { useEffect, useMemo, useState } from "react";
import {
  ONGROUND_LOGO_SURFACE,
  resolveOngroundLogoForSurface,
} from "./ongroundBranding";
import { downloadBookingConfirmationPdf } from "./publicBookingConfirmationPdf";
import {
  clearEsewaRecoveryContext,
  clearEsewaTransactionSubmitted,
  getEsewaRecoveryContext,
} from "./esewaSubmissionGuard";
import {
  clearRazorpayRecoveryContext,
  getRazorpayRecoveryContext,
} from "./razorpayRecovery";
import { resolveWebsiteCopy } from "./publicWebsiteContent";
import { getPublicBookingConfirmation } from "./publicVenueApi";
import {
  getPublicPaymentConfirmationContext,
} from "./publicPaymentConfirmationContext";

const MAX_RESULT_SLOTS = 24;

const cleanText = (value, maxLength = 300) =>
  String(value == null ? "" : value)
    .trim()
    .slice(0, maxLength);

export const getCountryAdminSupportContact = (venue) => ({
  email: cleanText(venue?.supportContact?.email, 160),
  phoneE164: cleanText(venue?.supportContact?.phoneE164, 50),
});

const readSearchValue = (searchParams, key, maxLength) =>
  cleanText(searchParams.get(key), maxLength);

const toFiniteNumber = (value, fallback = null) => {
  if (value === null || value === undefined || value === "") {
    return fallback;
  }

  const number = Number(value);
  return Number.isFinite(number) ? number : fallback;
};

const normalizeResultSlot = (value = {}) => {
  const date = cleanText(value?.date, 20);
  const start = cleanText(value?.start, 20);
  const end = cleanText(value?.end, 20);
  const currency = cleanText(value?.currency, 12).toUpperCase();
  const price = toFiniteNumber(
    value?.price ?? value?.amount ?? value?.totalAmount,
    null,
  );

  if (!date || !start || !end) {
    return null;
  }

  return {
    date,
    start,
    end,
    currency,
    price,
  };
};

const parseResultSlots = (value) => {
  const source = cleanText(value, 8000);
  if (!source) return [];

  try {
    const parsed = JSON.parse(source);
    if (!Array.isArray(parsed)) return [];

    return parsed
      .slice(0, MAX_RESULT_SLOTS)
      .map(normalizeResultSlot)
      .filter(Boolean);
  } catch {
    return [];
  }
};

const normalizeRecoverySlots = (value) =>
  Array.isArray(value)
    ? value
        .slice(0, MAX_RESULT_SLOTS)
        .map(normalizeResultSlot)
        .filter(Boolean)
    : [];


const parseYmdLocal = (value) => {
  const match = cleanText(value, 20).match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!match) return null;

  const [, year, month, day] = match;
  const parsed = new Date(
    Number(year),
    Number(month) - 1,
    Number(day),
    12,
    0,
    0,
    0,
  );

  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

const formatSlotTime = (value) => {
  const match = cleanText(value, 20).match(/^(\d{1,2}):(\d{2})$/);
  if (!match) return cleanText(value, 20);

  const hour = Number(match[1]);
  const minute = match[2];

  if (!Number.isInteger(hour) || hour < 0 || hour > 23) {
    return cleanText(value, 20);
  }

  const period = hour >= 12 ? "PM" : "AM";
  const displayHour = hour % 12 || 12;

  return `${displayHour}:${minute} ${period}`;
};

const formatBookingDate = (value) => {
  const parsed = parseYmdLocal(value);
  if (!parsed) return cleanText(value, 30);

  const weekdays = [
    "Sun",
    "Mon",
    "Tue",
    "Wed",
    "Thu",
    "Fri",
    "Sat",
  ];
  const months = [
    "Jan",
    "Feb",
    "Mar",
    "Apr",
    "May",
    "Jun",
    "Jul",
    "Aug",
    "Sep",
    "Oct",
    "Nov",
    "Dec",
  ];

  return `${weekdays[parsed.getDay()]}, ${parsed.getDate()} ${
    months[parsed.getMonth()]
  } ${parsed.getFullYear()}`;
};

const formatDay = (value) => {
  const parsed = parseYmdLocal(value);
  return parsed ? String(parsed.getDate()) : "";
};

const formatMonth = (value) => {
  const parsed = parseYmdLocal(value);
  return parsed
    ? new Intl.DateTimeFormat("en-IN", { month: "short" }).format(parsed)
    : "";
};

const formatBookingCreatedAt = (value, timeZone) => {
  const raw = cleanText(value, 80);
  if (!raw) return "";

  const date = new Date(raw);
  if (Number.isNaN(date.getTime())) return "";

  const venueTimeZone = cleanText(timeZone, 80) || "UTC";

  const formatWithTimezone = (resolvedTimeZone) => {
    const datePart = new Intl.DateTimeFormat("en-GB", {
      weekday: "short",
      day: "2-digit",
      month: "short",
      year: "numeric",
      timeZone: resolvedTimeZone,
    }).format(date);

    const timePart = new Intl.DateTimeFormat("en-US", {
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
      timeZone: resolvedTimeZone,
    }).format(date);

    return `${datePart} \u00b7 ${timePart}`;
  };

  try {
    return formatWithTimezone(venueTimeZone);
  } catch (_error) {
    return formatWithTimezone("UTC");
  }
};

const formatDateRange = (slots) => {
  const uniqueDates = Array.from(
    new Set(
      (slots || [])
        .map((slot) => cleanText(slot?.date, 20))
        .filter(Boolean),
    ),
  ).sort();

  if (!uniqueDates.length) return "";

  const first = parseYmdLocal(uniqueDates[0]);
  const last = parseYmdLocal(uniqueDates[uniqueDates.length - 1]);

  if (!first || !last) return uniqueDates[0];

  const months = [
    "Jan",
    "Feb",
    "Mar",
    "Apr",
    "May",
    "Jun",
    "Jul",
    "Aug",
    "Sep",
    "Oct",
    "Nov",
    "Dec",
  ];

  const firstLabel = `${
    months[first.getMonth()]
  } ${first.getDate()}`;

  if (uniqueDates.length === 1) {
    return firstLabel;
  }

  const sameMonth =
    first.getFullYear() === last.getFullYear() &&
    first.getMonth() === last.getMonth();

  const lastLabel = sameMonth
    ? String(last.getDate())
    : `${months[last.getMonth()]} ${last.getDate()}`;

  return `${firstLabel} \u2013 ${lastLabel}`;
};

const formatMoney = (currency, amount) => {
  const number = toFiniteNumber(amount, null);
  if (number === null) return "";

  const code = cleanText(currency, 12).toUpperCase() || "INR";
  const formatted = new Intl.NumberFormat("en-IN", {
    maximumFractionDigits: 2,
  }).format(number);

  return `${code} ${formatted}`;
};

const getVenueAddress = (venue) =>
  [
    venue?.location?.address,
    venue?.location?.city,
    venue?.location?.country,
  ]
    .filter(Boolean)
    .join(", ");

const getContactHref = (venue, website, venueHref) => {
  const phone = cleanText(venue?.contact?.e164, 40);

  if (phone) {
    return `tel:${phone}`;
  }

  const email = cleanText(website?.arena?.contactEmail, 160);

  if (email) {
    return `mailto:${email}`;
  }

  return `${venueHref}#contact`;
};

const groupResultSlotsByDate = (slots = []) => {
  const groups = [];
  const byDate = new Map();

  (Array.isArray(slots) ? slots : []).forEach((slot) => {
    const date = cleanText(slot?.date, 20);
    if (!date) return;

    if (!byDate.has(date)) {
      const group = { date, slots: [] };
      byDate.set(date, group);
      groups.push(group);
    }

    byDate.get(date).slots.push(slot);
  });

  return groups;
};

const escapeIcsText = (value) =>
  cleanText(value, 500)
    .replace(/\\/g, "\\\\")
    .replace(/\n/g, "\\n")
    .replace(/,/g, "\\,")
    .replace(/;/g, "\\;");

const toIcsDateTime = (date, time) => {
  const dateMatch = cleanText(date, 20).match(/^(\d{4})-(\d{2})-(\d{2})$/);
  const timeMatch = cleanText(time, 20).match(/^(\d{1,2}):(\d{2})$/);

  if (!dateMatch || !timeMatch) return "";

  const [, year, month, day] = dateMatch;
  const hour = String(Number(timeMatch[1])).padStart(2, "0");
  const minute = timeMatch[2];

  return `${year}${month}${day}T${hour}${minute}00`;
};

export const buildBookingCalendarIcs = ({
  slots = [],
  timezone,
  groundName,
  venueName,
  venueAddress,
  bookingReference,
  now = new Date(),
}) => {
  const timezoneId = cleanText(timezone, 80).replace(
    /[^A-Za-z0-9_+./-]/g,
    "",
  );

  if (!timezoneId) return "";

  const stamp = new Date(now)
    .toISOString()
    .replace(/[-:]/g, "")
    .replace(/\.\d{3}Z$/, "Z");

  const events = (slots || [])
    .map(normalizeResultSlot)
    .filter(Boolean)
    .map((slot, index) => {
      const start = toIcsDateTime(slot.date, slot.start);
      const end = toIcsDateTime(slot.date, slot.end);

      if (!start || !end) return "";

      return [
        "BEGIN:VEVENT",
        `UID:${escapeIcsText(
          `${bookingReference || "onground"}-${index + 1}@getonground.com`,
        )}`,
        `DTSTAMP:${stamp}`,
        `DTSTART;TZID=${timezoneId}:${start}`,
        `DTEND;TZID=${timezoneId}:${end}`,
        `SUMMARY:${escapeIcsText(
          `${groundName || "Venue booking"} - ${venueName || "Onground venue"}`,
        )}`,
        venueAddress
          ? `LOCATION:${escapeIcsText(venueAddress)}`
          : "",
        bookingReference
          ? `DESCRIPTION:${escapeIcsText(
              `Onground booking ${bookingReference}`,
            )}`
          : "",
        "END:VEVENT",
      ]
        .filter(Boolean)
        .join("\r\n");
    })
    .filter(Boolean);

  if (!events.length) return "";

  return [
    "BEGIN:VCALENDAR",
    "VERSION:2.0",
    "PRODID:-//Onground//Booking Confirmation//EN",
    "CALSCALE:GREGORIAN",
    `X-WR-TIMEZONE:${timezoneId}`,
    ...events,
    "END:VCALENDAR",
    "",
  ].join("\r\n");
};

export const parsePublicPaymentResultDetails = (search = "") => {
  const params = new URLSearchParams(search);

  return {
    bookingReference: readSearchValue(
      params,
      "booking_reference",
      80,
    ),
    groundName: readSearchValue(params, "booking_ground", 120),
    bookingWhen: readSearchValue(params, "booking_when", 500),
    createdAt: readSearchValue(params, "booking_created_at", 80),
    cancellationPolicy: readSearchValue(
      params,
      "booking_cancellation_policy",
      1600,
    ),
    failureReason: readSearchValue(params, "payment_reason", 180),
    currency: readSearchValue(params, "booking_currency", 12).toUpperCase(),
    subtotal: toFiniteNumber(
      readSearchValue(params, "booking_subtotal", 40),
      null,
    ),
    discountAmount: toFiniteNumber(
      readSearchValue(params, "booking_discount", 40),
      null,
    ),
    finalAmount: toFiniteNumber(
      readSearchValue(params, "booking_total", 40),
      null,
    ),
    promoCode: readSearchValue(params, "booking_promo", 80).toUpperCase(),
    slots: parseResultSlots(
      readSearchValue(params, "booking_slots", 8000),
    ),
  };
};

export const buildPaymentConfirmationText = ({
  bookingReference,
  venueName,
  groundName,
  bookingWhen,
  venueAddress,
  slots = [],
  currency = "INR",
  subtotal = null,
  discountAmount = null,
  finalAmount = null,
  promoCode = "",
}) => {
  const slotLines = (slots || []).flatMap((slot, index) => {
    const normalized = normalizeResultSlot(slot);
    if (!normalized) return [];

    const slotPrice = formatMoney(
      normalized.currency || currency,
      normalized.price,
    );

    return [
      `Slot ${index + 1}: ${formatBookingDate(normalized.date)} | ${formatSlotTime(
        normalized.start,
      )} - ${formatSlotTime(normalized.end)}${slotPrice ? ` | ${slotPrice}` : ""}`,
    ];
  });

  const pricingLines = [
    toFiniteNumber(subtotal, null) !== null
      ? `Subtotal: ${formatMoney(currency, subtotal)}`
      : "",
    toFiniteNumber(discountAmount, null) > 0
      ? `Discount${promoCode ? ` (${promoCode})` : ""}: -${formatMoney(
          currency,
          discountAmount,
        )}`
      : "",
    toFiniteNumber(finalAmount, null) !== null
      ? `Total paid: ${formatMoney(currency, finalAmount)}`
      : "",
  ].filter(Boolean);

  return [
    "ONGROUND BOOKING CONFIRMATION",
    "=============================",
    bookingReference
      ? `Booking reference: ${bookingReference}`
      : "",
    venueName ? `Venue: ${venueName}` : "",
    groundName ? `Ground: ${groundName}` : "",
    bookingWhen ? `When: ${bookingWhen}` : "",
    venueAddress ? `Address: ${venueAddress}` : "",
    ...slotLines,
    ...pricingLines,
    "",
    "Payment status: Confirmed",
    "Keep this confirmation for check-in and support.",
  ]
    .filter((line, index, lines) => {
      if (line !== "") return true;
      return index > 0 && lines[index - 1] !== "";
    })
    .join("\n")
    .trim();
};

const RESULT_META = {
  pending: {
    eyebrow: "Verification pending",
    title: "We\u2019re confirming your payment",
    message:
      "Your payment response has been received, but the booking is not confirmed yet. Do not pay again. Return to your booking to check the same payment safely.",
  },
  error: {
    eyebrow: "Verification issue",
    title: "We couldn\u2019t confirm the payment result",
    message:
      "Please contact the venue before trying another payment so this booking can be checked safely.",
  },
};

const PublicPaymentResult = ({
  resultType,
  venue,
  website,
  slug,
  search,
  venueHrefOverride = "",
  primaryColor = "#33A444",
  accentColor = "#111827",
  onTryAgain,
  onChooseDifferentTime,
  paymentGateway = "ESEWA",
}) => {
  const [downloadLabel, setDownloadLabel] = useState("");
  const [copyReferenceLabel, setCopyReferenceLabel] = useState("");
  const [calendarLabel, setCalendarLabel] = useState("");
  const [policyOpen, setPolicyOpen] = useState(false);
  const [verifiedConfirmation, setVerifiedConfirmation] = useState(null);
  const [confirmationState, setConfirmationState] = useState(
    resultType === "success" ? "checking" : "not_required",
  );
  const [confirmationMessage, setConfirmationMessage] = useState("");

  useEffect(() => {
    if (!policyOpen) return undefined;

    const previousOverflow = document.body.style.overflow;
    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        setPolicyOpen(false);
      }
    };

    document.body.style.overflow = "hidden";
    window.addEventListener("keydown", handleKeyDown);

    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [policyOpen]);

  const t = (key, fallback) =>
    resolveWebsiteCopy(website, key, fallback);

  const queryDetails = useMemo(
    () => parsePublicPaymentResultDetails(search),
    [search],
  );

  const isDevelopmentSuccessPreview = useMemo(() => {
    if (process.env.NODE_ENV !== "development") return false;

    const params = new URLSearchParams(search || "");
    return (
      resultType === "success" &&
      params.get("payment_preview")?.trim().toLowerCase() === "success"
    );
  }, [resultType, search]);

  useEffect(() => {
    if (resultType !== "success") {
      setVerifiedConfirmation(null);
      setConfirmationState("not_required");
      setConfirmationMessage("");
      return undefined;
    }

    if (isDevelopmentSuccessPreview) {
      setVerifiedConfirmation({
        bookingReference:
          queryDetails.bookingReference || "PREVIEW-001",
        groundName:
          queryDetails.groundName || "Preview Ground",
        createdAt: queryDetails.createdAt,
        currency:
          queryDetails.currency || "INR",
        subtotal: queryDetails.subtotal,
        discountAmount: queryDetails.discountAmount,
        finalAmount: queryDetails.finalAmount,
        promoCode: queryDetails.promoCode,
        slots: queryDetails.slots,
      });
      setConfirmationState("confirmed");
      setConfirmationMessage("");
      return undefined;
    }

    if (typeof window === "undefined") {
      return undefined;
    }

    const context = getPublicPaymentConfirmationContext(
      window.sessionStorage,
    );
    const expectedGateway = cleanText(paymentGateway, 20).toUpperCase();

    if (
      !context ||
      context.slug !== slug ||
      context.paymentGateway !== expectedGateway
    ) {
      setVerifiedConfirmation(null);
      setConfirmationState("invalid");
      setConfirmationMessage(
        "This booking confirmation cannot be verified from this link. Return to the venue and complete the payment flow again if needed.",
      );
      return undefined;
    }

    let active = true;
    setVerifiedConfirmation(null);
    setConfirmationState("checking");
    setConfirmationMessage("");

    getPublicBookingConfirmation(
      slug,
      context.reservationId,
      context.confirmationToken,
    )
      .then((data) => {
        if (!active) return;

        if (data?.state === "pending") {
          setConfirmationState("pending");
          setConfirmationMessage(
            data?.message ||
              "Payment confirmation is still being processed. Do not pay again.",
          );
          return;
        }

        const confirmation = data?.confirmation;
        if (data?.state !== "confirmed" || !confirmation) {
          setConfirmationState("invalid");
          setConfirmationMessage(
            "This booking is not a verified paid booking.",
          );
          return;
        }

        const requestedReference = cleanText(
          queryDetails.bookingReference,
          80,
        ).toUpperCase();
        const authoritativeReference = cleanText(
          confirmation.bookingReference,
          80,
        ).toUpperCase();

        if (
          requestedReference &&
          requestedReference !== authoritativeReference
        ) {
          setConfirmationState("invalid");
          setConfirmationMessage(
            "This confirmation link was modified and does not match the verified booking.",
          );
          return;
        }

        setVerifiedConfirmation(confirmation);
        setConfirmationState("confirmed");
      })
      .catch((error) => {
        if (!active) return;

        const responseData = error?.response?.data || {};
        const state = cleanText(responseData?.state, 40).toLowerCase();

        if (state === "pending") {
          setConfirmationState("pending");
          setConfirmationMessage(
            responseData?.message ||
              "Payment confirmation is still being processed. Do not pay again.",
          );
          return;
        }

        setConfirmationState("invalid");
        setConfirmationMessage(
          responseData?.message ||
            "This booking confirmation could not be verified.",
        );
      });

    return () => {
      active = false;
    };
  }, [
    isDevelopmentSuccessPreview,
    paymentGateway,
    queryDetails,
    resultType,
    slug,
  ]);

  const recoverySnapshot = useMemo(() => {
    if (typeof window === "undefined") return null;

    const recovery =
      paymentGateway === "RAZORPAY"
        ? getRazorpayRecoveryContext(window.sessionStorage)
        : getEsewaRecoveryContext(window.sessionStorage);

    return recovery?.bookingSnapshot || null;
  }, [paymentGateway]);

  useEffect(() => {
    if (resultType !== "success") {
      return;
    }

    if (paymentGateway === "RAZORPAY") {
      clearRazorpayRecoveryContext(
        window.sessionStorage,
      );
      return;
    }

    const recovery =
      getEsewaRecoveryContext(
        window.sessionStorage,
      );

    if (recovery?.transactionUuid) {
      clearEsewaTransactionSubmitted(
        window.sessionStorage,
        recovery.transactionUuid,
      );
    }

    clearEsewaRecoveryContext(
      window.sessionStorage,
    );
  }, [paymentGateway, resultType]);

  const venueHref =
    cleanText(venueHrefOverride, 2048) ||
    `/venues/${encodeURIComponent(slug || "")}`;

  const venueName = cleanText(venue?.name, 120) || "Onground venue";
  const venueAddress = getVenueAddress(venue);
  const countrySupport = getCountryAdminSupportContact(venue);
  const supportEmail = countrySupport.email;
  const supportPhone = countrySupport.phoneE164;
  const contactHref = getContactHref(
    { contact: { e164: supportPhone } },
    { arena: { contactEmail: supportEmail } },
    venueHref,
  );
  const hasSupportContact = Boolean(supportEmail || supportPhone);

  const trustedDetails =
    resultType === "success"
      ? confirmationState === "confirmed" && verifiedConfirmation
        ? {
            bookingReference: cleanText(verifiedConfirmation.bookingReference, 80),
            groundName: cleanText(verifiedConfirmation.groundName, 120),
            bookingWhen: "",
            createdAt: cleanText(verifiedConfirmation.createdAt, 80),
            failureReason: "",
            currency: cleanText(verifiedConfirmation.currency, 12).toUpperCase(),
            subtotal: toFiniteNumber(verifiedConfirmation.subtotal, null),
            discountAmount: toFiniteNumber(
              verifiedConfirmation.discountAmount,
              null,
            ),
            finalAmount: toFiniteNumber(verifiedConfirmation.finalAmount, null),
            promoCode: cleanText(verifiedConfirmation.promoCode, 80).toUpperCase(),
            slots: Array.isArray(verifiedConfirmation.slots)
              ? verifiedConfirmation.slots
                  .slice(0, MAX_RESULT_SLOTS)
                  .map(normalizeResultSlot)
                  .filter(Boolean)
              : [],
          }
        : {
            bookingReference: "",
            groundName: "",
            bookingWhen: "",
            createdAt: "",
            failureReason: "",
            currency: "",
            subtotal: null,
            discountAmount: null,
            finalAmount: null,
            promoCode: "",
            slots: [],
          }
      : queryDetails;

  const recoverySlots =
    resultType === "success"
      ? []
      : normalizeRecoverySlots(recoverySnapshot?.selectedItems);

  const slots = trustedDetails.slots.length
    ? trustedDetails.slots
    : recoverySlots;

  const groupedSlots = groupResultSlotsByDate(slots);

  const recoveryPromo =
    resultType !== "success" &&
    recoverySnapshot?.promoPreview &&
    typeof recoverySnapshot.promoPreview === "object"
      ? recoverySnapshot.promoPreview
      : null;

  const currency =
    trustedDetails.currency ||
    cleanText(slots[0]?.currency, 12).toUpperCase() ||
    cleanText(recoveryPromo?.currency, 12).toUpperCase() ||
    "INR";

  const pricedSlots = slots.filter(
    (slot) =>
      toFiniteNumber(slot?.price, null) !== null,
  );
  const slotsSubtotal = pricedSlots.reduce(
    (sum, slot) =>
      sum + (toFiniteNumber(slot?.price, 0) || 0),
    0,
  );
  const hasPricingEvidence = Boolean(
    trustedDetails.subtotal !== null ||
      trustedDetails.discountAmount !== null ||
      trustedDetails.finalAmount !== null ||
      toFiniteNumber(recoveryPromo?.subtotal, null) !== null ||
      toFiniteNumber(recoveryPromo?.discountAmount, null) !== null ||
      toFiniteNumber(recoveryPromo?.finalAmount, null) !== null ||
      pricedSlots.length,
  );

  const subtotal = hasPricingEvidence
    ? trustedDetails.subtotal ??
      toFiniteNumber(recoveryPromo?.subtotal, null) ??
      slotsSubtotal
    : null;

  const discountAmount = hasPricingEvidence
    ? Math.max(
        0,
        trustedDetails.discountAmount ??
          toFiniteNumber(recoveryPromo?.discountAmount, 0) ??
          0,
      )
    : null;

  const finalAmount = hasPricingEvidence
    ? Math.max(
        0,
        trustedDetails.finalAmount ??
          toFiniteNumber(recoveryPromo?.finalAmount, null) ??
          Math.max(
            0,
            (subtotal || 0) - (discountAmount || 0),
          ),
      )
    : null;

  const promoCode =
    trustedDetails.promoCode ||
    cleanText(recoveryPromo?.code, 80).toUpperCase() ||
    cleanText(recoverySnapshot?.promoCode, 80).toUpperCase();

  const bookingWhen =
    trustedDetails.bookingWhen ||
    slots
      .map(
        (slot) =>
          `${formatBookingDate(slot.date)} \u00b7 ${formatSlotTime(
            slot.start,
          )} \u2013 ${formatSlotTime(slot.end)}`,
      )
      .join("; ");

  const groundName =
    trustedDetails.groundName ||
    (resultType === "success"
      ? ""
      : cleanText(recoverySnapshot?.groundName, 120));

  const dateRange = formatDateRange(slots);
  const venueTimezone = cleanText(
    venue?.policies?.timezone,
    80,
  );
  const createdAtLabel = formatBookingCreatedAt(
    trustedDetails.createdAt,
    venueTimezone,
  );
  // PublicVenuePage already uses venue.googleMapsUrl for "Open in Maps".
  // Use that same API-backed URL here; never manufacture a map URL.
  const mapsHref = cleanText(venue?.googleMapsUrl, 2048);

  const venueCancellationPolicy = cleanText(
    venue?.policies?.cancellationPolicy,
    1600,
  );
  const cancellationPolicy =
    isDevelopmentSuccessPreview &&
    queryDetails.cancellationPolicy
      ? cleanText(queryDetails.cancellationPolicy, 1600)
      : venueCancellationPolicy;
  const hasCancellationPolicy = Boolean(cancellationPolicy);

  const handleDownload = async () => {
    const safeReference =
      trustedDetails.bookingReference
        .replace(/[^a-zA-Z0-9_-]+/g, "-")
        .replace(/^-+|-+$/g, "") ||
      "booking";

    try {
      await downloadBookingConfirmationPdf(
        `onground-${safeReference}-confirmation.pdf`,
        {
          bookingReference: trustedDetails.bookingReference,
          venueName,
          groundName,
          bookingWhen,
          venueAddress,
          createdAt: createdAtLabel,
          slots,
          currency,
          subtotal,
          discountAmount,
          finalAmount,
          promoCode,
          cancellationPolicy,
          supportEmail,
          supportPhone,
          primaryColor,
          accentColor,
        },
      );

      setDownloadLabel(
        t("paymentDownloadedLabel", "Downloaded"),
      );
    } catch (_error) {
      setDownloadLabel("");
    }
  };

  const calendarTimezone = venueTimezone;

  const handleCopyReference = async () => {
    const reference = cleanText(
      trustedDetails.bookingReference,
      80,
    );

    if (!reference) return;

    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(reference);
      } else {
        const textArea = document.createElement("textarea");
        textArea.value = reference;
        textArea.setAttribute("readonly", "");
        textArea.style.position = "fixed";
        textArea.style.opacity = "0";
        document.body.appendChild(textArea);
        textArea.select();

        const copied = document.execCommand("copy");
        textArea.remove();

        if (!copied) {
          throw new Error("Copy failed");
        }
      }

      setCopyReferenceLabel("Copied");
      window.setTimeout(() => {
        setCopyReferenceLabel("");
      }, 1600);
    } catch (_error) {
      setCopyReferenceLabel("Copy failed");
      window.setTimeout(() => {
        setCopyReferenceLabel("");
      }, 1600);
    }
  };

  const handleAddToCalendar = () => {
    const ics = buildBookingCalendarIcs({
      slots,
      timezone: calendarTimezone,
      groundName,
      venueName,
      venueAddress,
      bookingReference: trustedDetails.bookingReference,
    });

    if (!ics) return;

    const blob = new Blob([ics], {
      type: "text/calendar;charset=utf-8",
    });
    const objectUrl = URL.createObjectURL(blob);
    const link = document.createElement("a");
    const safeReference =
      trustedDetails.bookingReference
        .replace(/[^a-zA-Z0-9_-]+/g, "-")
        .replace(/^-+|-+$/g, "") ||
      "booking";

    link.href = objectUrl;
    link.download = `onground-${safeReference}.ics`;
    link.style.display = "none";

    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(objectUrl);

    setCalendarLabel("Calendar downloaded");
    window.setTimeout(() => {
      setCalendarLabel("");
    }, 1800);
  };

  const effectiveResultType =
    resultType === "success"
      ? confirmationState === "confirmed"
        ? "success"
        : ["checking", "pending"].includes(confirmationState)
          ? "pending"
          : "error"
      : resultType;

  if (effectiveResultType === "success") {
    return (
      <main
        className="pv-manager-confirm-page"
        style={{
          "--pv-manager-primary": primaryColor,
          "--pv-manager-accent": accentColor,
        }}
      >
        <div className="pv-manager-confirm-shell">
          <header className="pv-manager-confirm-brandrow">
            <a className="pv-manager-confirm-brand-l" href={venueHref}>
              <span className="pv-manager-confirm-ring">
                {venueName.charAt(0).toUpperCase()}
              </span>
              <span className="pv-manager-confirm-brand-name">
                {venueName}
              </span>
            </a>

            <div className="pv-manager-confirm-brand-r">
              <small>SECURE BOOKING</small>
              <span className="pv-manager-confirm-brand-byline">
                <span>BY</span>
                <img
                  src={resolveOngroundLogoForSurface(
                    ONGROUND_LOGO_SURFACE.LIGHT,
                  )}
                  alt="Onground"
                />
              </span>
            </div>
          </header>

          <section
            className="pv-manager-confirm-ticket"
            aria-live="polite"
          >
            <div className="pv-manager-confirm-ticket-main">
              <div className="pv-manager-confirm-ticket-left">
                <div className="pv-manager-confirm-kicker">
                  Booking confirmed
                </div>

                <h1 className="pv-manager-confirm-title">
                  Booking
                  <br />
                  Confirmation
                </h1>

                <p className="pv-manager-confirm-line">
                  Your payment was verified and your booking is confirmed.
                </p>

                {createdAtLabel && (
                  <div className="pv-manager-confirm-created">
                    <span
                      className="pv-manager-confirm-created__icon"
                      aria-hidden="true"
                    >
                      <svg viewBox="0 0 24 24">
                        <circle cx="12" cy="12" r="8.25" />
                        <path d="M12 7.8v4.65l3.2 1.9" />
                      </svg>
                    </span>

                    <span className="pv-manager-confirm-created__label">
                      Created at
                    </span>

                    <span
                      className="pv-manager-confirm-created__divider"
                      aria-hidden="true"
                    />

                    <time dateTime={trustedDetails.createdAt}>
                      {createdAtLabel}
                    </time>
                  </div>
                )}

                <div className="pv-manager-confirm-venue-name">
                  {[groundName, venueName]
                    .filter(Boolean)
                    .join(" \u2014 ")}
                </div>

                {venueAddress && (
                  <div className="pv-manager-confirm-venue-addr">
                    {venueAddress}
                  </div>
                )}

                {slots.length ? (
                  <div className="pv-manager-confirm-booking-groups">
                    {groupedSlots.map((group) => (
                      <section
                        className="pv-manager-confirm-booking-group"
                        key={group.date}
                      >
                        <div className="pv-manager-confirm-booking-group__head">
                          <span className="pv-manager-confirm-bi-date">
                            <strong>{formatDay(group.date)}</strong>
                            <small>{formatMonth(group.date)}</small>
                          </span>

                          <span className="pv-manager-confirm-booking-group__date">
                            <strong>{formatBookingDate(group.date)}</strong>
                            <small>
                              {group.slots.length}{" "}
                              {group.slots.length === 1 ? "slot" : "slots"}
                            </small>
                          </span>
                        </div>

                        <div className="pv-manager-confirm-booking-group__slots">
                          {group.slots.map((slot, index) => (
                            <div
                              className="pv-manager-confirm-booking-group__slot"
                              key={`${slot.date}-${slot.start}-${slot.end}-${index}`}
                            >
                              <span className="pv-manager-confirm-booking-group__time">
                                {formatSlotTime(slot.start)}{" - "}
                                {formatSlotTime(slot.end)}
                              </span>

                              {slot.price !== null && (
                                <strong className="pv-manager-confirm-booking-group__price">
                                  {formatMoney(
                                    slot.currency || currency,
                                    slot.price,
                                  )}
                                </strong>
                              )}
                            </div>
                          ))}
                        </div>
                      </section>
                    ))}
                  </div>
                ) : (
                  <div className="pv-manager-confirm-booking-fallback">
                    {bookingWhen || "Confirmed"}
                  </div>
                )}
              </div>

              <aside className="pv-manager-confirm-datebadge">
                <span
                  className="pv-manager-confirm-star"
                  aria-hidden="true"
                >
                  <svg viewBox="0 0 24 24" fill="none">
                    <path
                      d="M5 12.5L10 17.5L19 7.5"
                      stroke="white"
                      strokeWidth="3"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </span>

                <strong className="pv-manager-confirm-badge-day">
                  {slots.length || 1}
                </strong>
                <span className="pv-manager-confirm-badge-mon">
                  Bookings
                </span>
                <span className="pv-manager-confirm-badge-time">
                  {dateRange || "Confirmed"}
                </span>

                <div className="pv-manager-confirm-refwrap">
                  <span className="pv-manager-confirm-reflbl">
                    Reference No
                  </span>
                  {trustedDetails.bookingReference ? (
                    <button
                      type="button"
                      className="pv-manager-confirm-refcode"
                      onClick={handleCopyReference}
                      aria-label="Copy booking reference"
                      title="Copy booking reference"
                    >
                      {copyReferenceLabel ||
                        trustedDetails.bookingReference}
                    </button>
                  ) : (
                    <code className="pv-manager-confirm-refcode">
                      {"\u2014"}
                    </code>
                  )}
                </div>
              </aside>
            </div>

            <div className="pv-manager-confirm-snip" aria-hidden="true" />
            <div className="pv-manager-confirm-dashline" />

            {hasPricingEvidence && (
              <section className="pv-manager-confirm-pricing">
                <h2>Payment Summary</h2>

                <div className="pv-manager-confirm-pricing__row">
                  <span>Sub Total</span>
                  <strong>{formatMoney(currency, subtotal)}</strong>
                </div>

                {discountAmount > 0 && (
                  <div className="pv-manager-confirm-pricing__row is-discount">
                    <span>
                      Discount
                      {promoCode && <code>{promoCode}</code>}
                    </span>
                    <strong>
                      - {formatMoney(currency, discountAmount)}
                    </strong>
                  </div>
                )}

                <div className="pv-manager-confirm-total-paid">
                  <span>Total Paid</span>
                  <strong>{formatMoney(currency, finalAmount)}</strong>
                </div>
              </section>
            )}

            {hasCancellationPolicy && (
              <>
                <button
                  type="button"
                  className="pv-ticket-policy-trigger"
                  onClick={() => setPolicyOpen(true)}
                  aria-haspopup="dialog"
                >
                  <span
                    className="pv-ticket-policy-trigger__icon"
                    aria-hidden="true"
                  >
                    <svg viewBox="0 0 24 24">
                      <path d="M12 3 4.8 6v5.5c0 4.7 3 7.9 7.2 9.5 4.2-1.6 7.2-4.8 7.2-9.5V6L12 3Z" />
                      <path d="M9 12.2 11 14l4-4.4" />
                    </svg>
                  </span>

                  <span className="pv-ticket-policy-trigger__copy">
                    <strong>Cancellation policy</strong>
                    <small>
                      View venue terms for this booking
                    </small>
                  </span>

                  <span className="pv-ticket-policy-trigger__action">
                    View policy
                    <svg viewBox="0 0 24 24" aria-hidden="true">
                      <path d="m9 6 6 6-6 6" />
                    </svg>
                  </span>
                </button>

                {policyOpen &&
                  createPortal(
                    <div
                      className="pv-policy-modal-backdrop"
                      role="presentation"
                      style={{
                        "--pv-ticket-primary": primaryColor,
                        "--pv-ticket-accent": accentColor,
                      }}
                      onMouseDown={(event) => {
                        if (event.target === event.currentTarget) {
                          setPolicyOpen(false);
                        }
                      }}
                    >
                      <section
                        className="pv-policy-modal"
                        role="dialog"
                        aria-modal="true"
                        aria-labelledby="pv-policy-modal-title"
                      >
                        <header className="pv-policy-modal__header">
                          <span
                            className="pv-policy-modal__mark"
                            aria-hidden="true"
                          >
                            <svg viewBox="0 0 24 24">
                              <path d="M12 3 4.8 6v5.5c0 4.7 3 7.9 7.2 9.5 4.2-1.6 7.2-4.8 7.2-9.5V6L12 3Z" />
                              <path d="M9 12.2 11 14l4-4.4" />
                            </svg>
                          </span>

                          <div>
                            <span>Venue terms</span>
                            <h2 id="pv-policy-modal-title">
                              Cancellation policy
                            </h2>
                            <p>{venueName}</p>
                          </div>

                          <button
                            type="button"
                            className="pv-policy-modal__close"
                            onClick={() => setPolicyOpen(false)}
                            aria-label="Close cancellation policy"
                            autoFocus
                          >
                            <svg viewBox="0 0 24 24" aria-hidden="true">
                              <path d="m6 6 12 12M18 6 6 18" />
                            </svg>
                          </button>
                        </header>

                        <div className="pv-policy-modal__body">
                          <div className="pv-policy-modal__notice">
                            <span aria-hidden="true">i</span>
                            <p>
                              These cancellation terms are provided by
                              the venue for this booking.
                            </p>
                          </div>

                          <div className="pv-policy-modal__policy">
                            <span>Policy details</span>
                            <p>{cancellationPolicy}</p>
                          </div>
                        </div>

                        <footer className="pv-policy-modal__footer">
                          <button
                            type="button"
                            onClick={() => setPolicyOpen(false)}
                          >
                            Got it
                          </button>
                        </footer>
                      </section>
                    </div>,
                    document.body,
                  )}
              </>
            )}

            {hasSupportContact && (
              <footer className="pv-manager-confirm-footerbar">
                <strong>Need help with this booking?</strong>
                <span className="pv-manager-confirm-footerbar__contacts">
                  {supportEmail && (
                    <a href={`mailto:${supportEmail}`}>
                      Email: {supportEmail}
                    </a>
                  )}
                  {supportEmail && supportPhone && (
                    <span aria-hidden="true">{"\u00b7"}</span>
                  )}
                  {supportPhone && (
                    <a href={`tel:${supportPhone}`}>
                      Phone: {supportPhone}
                    </a>
                  )}
                </span>
              </footer>
            )}
          </section>

          <div className="pv-manager-confirm-postactions">
            <button
              type="button"
              className="is-primary"
              onClick={handleDownload}
            >
              {downloadLabel ||
                t(
                  "paymentDownloadConfirmationLabel",
                  "Download confirmation",
                )}
            </button>

            {slots.length > 0 && calendarTimezone && (
              <button
                type="button"
                onClick={handleAddToCalendar}
                aria-label="Add to calendar"
                title={`Downloads one calendar file containing all booked slots in ${calendarTimezone}`}
              >
                {calendarLabel || "Add to calendar"}
              </button>
            )}

            {mapsHref && (
              <a
                href={mapsHref}
                target="_blank"
                rel="noreferrer"
              >
                Get directions
              </a>
            )}
          </div>
        </div>
      </main>
    );
  }

  if (effectiveResultType === "failed") {
    const failureReason =
      trustedDetails.failureReason || "Payment not completed";

    return (
      <main
        className="pv-manager-failure-page"
        style={{
          "--pv-manager-primary": primaryColor,
          "--pv-manager-accent": accentColor,
        }}
      >
        <section
          className="pv-manager-failure-card"
          role="alert"
          aria-live="polite"
        >
          <div className="pv-manager-failure-top">
            <div
              className="pv-manager-failure-brand"
              aria-label="Secure booking by Onground"
            >
              <span className="pv-manager-failure-brand-label">
                SECURE BOOKING
              </span>

              <span className="pv-manager-failure-brand-byline">
                <span>BY</span>
                <img
                  src={resolveOngroundLogoForSurface(
                    ONGROUND_LOGO_SURFACE.LIGHT,
                  )}
                  alt="Onground"
                />
              </span>
            </div>

            <div
              className="pv-manager-failure-icon"
              aria-hidden="true"
            >
              <svg
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.2"
                strokeLinecap="round"
              >
                <circle cx="12" cy="12" r="9" />
                <path d="M15 9l-6 6M9 9l6 6" />
              </svg>
            </div>

            <div className="pv-manager-failure-eyebrow">
              Booking unsuccessful
            </div>

            <h1>We couldn&apos;t confirm your booking</h1>

            <p className="pv-manager-failure-sub">
              Your payment wasn&apos;t completed and this slot
              hasn&apos;t been reserved. No charges were made.
            </p>
          </div>

          <div className="pv-manager-failure-detailbox">
            <div className="pv-manager-failure-detail-row">
              <span className="k">Venue</span>
              <span className="v">{venueName}</span>
            </div>

            <div className="pv-manager-failure-detail-row pv-manager-failure-detail-row--requested">
              <span className="k">
                {slots.length === 1 ? "Requested slot" : "Requested slots"}
              </span>

              <div className="v pv-manager-failure-requested-slots">
                {groupedSlots.length > 0 ? (
                  groupedSlots.map((group) => (
                    <div
                      className="pv-manager-failure-requested-group"
                      key={group.date}
                    >
                      <strong className="pv-manager-failure-requested-date">
                        {formatBookingDate(group.date)}
                      </strong>

                      <div className="pv-manager-failure-requested-times">
                        {group.slots.map((slot, index) => (
                          <span
                            className="pv-manager-failure-requested-time"
                            key={`${slot.date}-${slot.start}-${slot.end}-${index}`}
                          >
                            {formatSlotTime(slot.start)}
                            {" \u2013 "}
                            {formatSlotTime(slot.end)}
                          </span>
                        ))}
                      </div>
                    </div>
                  ))
                ) : (
                  <span className="pv-manager-failure-requested-fallback">
                    {bookingWhen || "\u2014"}
                  </span>
                )}
              </div>
            </div>

            <div className="pv-manager-failure-detail-row">
              <span className="k">Reason</span>
              <span className="v reason">{failureReason}</span>
            </div>
          </div>

          <div className="pv-manager-failure-actions">
            <button
              type="button"
              className="pv-manager-failure-btn pv-manager-failure-btn-primary"
              onClick={onTryAgain}
              aria-label="Try again"
            >
              <svg
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
              >
                <path d="M1 4v6h6M23 20v-6h-6" />
                <path d="M20.5 9A9 9 0 005.6 5.6L1 10M3.5 15a9 9 0 0014.9 3.4L23 14" />
              </svg>
              Try again
            </button>

            <button
              type="button"
              className="pv-manager-failure-btn pv-manager-failure-btn-ghost"
              onClick={onChooseDifferentTime}
              aria-label="Choose a different time"
            >
              Choose a different time
            </button>
          </div>

          {hasSupportContact && (
            <div className="pv-manager-failure-foot">
              Still having trouble?{" "}
              <a href={contactHref}>Contact support</a>
            </div>
          )}
        </section>
      </main>
    );
  }

  const baseResult =
    RESULT_META[effectiveResultType] || RESULT_META.error;
  const result =
    confirmationMessage && ["pending", "error"].includes(effectiveResultType)
      ? { ...baseResult, message: confirmationMessage }
      : baseResult;

  return (
    <main className="pv-result-state-page">
      <section className="pv-result-state-card" role="alert" aria-live="polite">
        <img
          className="pv-result-state-logo"
          src={resolveOngroundLogoForSurface(
            ONGROUND_LOGO_SURFACE.LIGHT,
          )}
          alt="Onground"
        />

        <span className="pv-result-state-eyebrow">
          {result.eyebrow}
        </span>

        <h1>{result.title}</h1>
        <p>{result.message}</p>

        <div className="pv-result-state-actions">
          {effectiveResultType === "pending" ? (
            <button type="button" onClick={onTryAgain}>
              Return to booking
            </button>
          ) : (
            <a href={venueHref}>Back to {venueName}</a>
          )}

          <a href={contactHref}>Contact support</a>
        </div>
      </section>
    </main>
  );
};

export default PublicPaymentResult;
